import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { get, isEmpty, remove, cloneDeep, reduce, sumBy } from 'lodash'

import { centsToDollars, formatCartItemModifiers } from '../../utils/formatters'

import styles from './ReceiptDetails.module.scss'

const getGroupedItems = (order) => {
  const itemModels = get(cloneDeep(order), 'itemModels', [])

  let result = []
  for (let i = 0; i < itemModels.length; i++) {
    const item = itemModels[i];
    if (item._used) continue
    for (let j = i+1; j < itemModels.length; j++) {
      const insideItem = itemModels[j];
      if (item.id === insideItem.id && item.modifiersString === insideItem.modifiersString) {
        item.quantity++
        insideItem._used = true
      }
    }
    result.push(item)
  }

  return result
}

const getTaxItemModels = (nonExemptableTaxRules) => {
  return reduce(nonExemptableTaxRules, (models, taxRule) => {
    if (taxRule.display) {
      return (
        <div className={styles.row}>
            <span>{taxRule.name}</span>
            <span className={styles.price}>{centsToDollars(taxRule.taxAmountInCents)}</span>
        </div>
      )
    }
    return models
  }, [])
}

const getTotalInCents = (order) => {
  const itemModels = get(cloneDeep(order), 'itemModels', [])

  const total = reduce(itemModels, (accumulator, item) => {
    const quantity = get(item, 'quantity', 0)
    const itemModifiers = get(item, 'modifiers', [])
    const itemSubtotal = get(item, 'priceInCents', 0) * quantity

    const modifiersSubtotal = reduce(itemModifiers, (acc, modifierItem) => {
      const priceInCents = get(modifierItem, 'defaultPriceInCents', 0)
      return acc + priceInCents * quantity
    }, 0)

    const lineItemTotal = itemSubtotal + modifiersSubtotal

    return accumulator + lineItemTotal
  }, 0)

  return total
}

const ReceiptDetails = ({ className, menuName, qrCodeData, order, revenueCenter }) => {
  const classNames = cn(styles.receiptDetails, className)
  let itemModels = getGroupedItems(order)
  const tip = get(order, 'tipAmountInCents', 0)
  let subtotalInCents = getTotalInCents(order)
  let grandTotal = subtotalInCents + tip

  let discountDisplay = undefined
  let serviceChargeDisplay = undefined
  let serviceChargeName = undefined
  let salesTax = 0
  let nonExemptableTaxLineItems = []
  const remoteOrderTotal = order?.remoteOrderTotal
  if (!isEmpty(remoteOrderTotal)) {
    discountDisplay = remoteOrderTotal.discountDisplay
    serviceChargeDisplay = remoteOrderTotal.serviceChargeDisplay
    serviceChargeName = remoteOrderTotal.serviceChargeName
    if (order?.exemptionOverrides?.applySalesTax) {
      salesTax = remoteOrderTotal?.taxAmountInCents ?? 0
    }
    nonExemptableTaxLineItems = (getTaxItemModels(remoteOrderTotal?.nonExemptableTaxRules))
    grandTotal = (remoteOrderTotal?.totalAmountInCents ?? subtotalInCents) + tip
  }

  const isRichCheckoutOrder = () => get(order, 'isRichCheckout', false)
  const hasCardReceipt = () => !!get(order, 'cardReaderData.receipt')

  const formattedCardData = () => {
    const receiptText = get(order, 'cardReaderData.receipt')
    if (isEmpty(receiptText)) return []

    let byLine = receiptText.split(/\r?\n/)
    remove(byLine, (line) => isEmpty(line))

    // non-emv (e.g., debit) only has saleInfo
    const cardInfoIndex = byLine.findIndex((line) => line.includes("****") && line.includes("EMV SALE"))
    const saleInfo = byLine.slice(0, cardInfoIndex - 1)
    const cardInfo = byLine.slice(cardInfoIndex, byLine.length - 1)

    return [saleInfo, cardInfo]
  }

  const renderRvcName = () => {
    const name = revenueCenter?.name ?? ""

    if (name.includes("Unassigned")) return null

    return <div className={styles.suite}>{revenueCenter.name}</div>
  }

  return (
    <div className={classNames}>
      <div className={styles.header}>
        {renderRvcName()}
        <div className={styles.title}>{`Order ${order.orderNumber}`}</div>
        <div className={styles.subTitle}>Thanks! Enter your email for a copy of your receipt.</div>
      </div>

      <div className={styles.lineItems}>
        {itemModels.map((item) => {
          const name = item.name
          const quantity = item.quantity
          const price = item.price

          return (
              <div className={styles.row} key={item.id}>
              <div className={styles.columnContainer}>
                <div className={styles.column}>
                  <span className={styles.quantity}>{!!quantity && quantity}</span>
                </div>
                <div className={styles.column}>
                  <span>{name}</span>
                  <span className={styles.modifiers}>{formatCartItemModifiers(item.modifiers, item.notes)}</span>
                </div>
              </div>
              { !isRichCheckoutOrder() && <span className={styles.price}>{price}</span> }
            </div>
          )
        })}
      </div>

      { !isRichCheckoutOrder() && (
        <div className={styles.total}>
          <div className={styles.row}>
            <div>
              <span>{`${sumBy(itemModels, 'quantity')} `}</span>
              <span>Subtotal</span>
            </div>
            <span className={styles.price}>{centsToDollars(subtotalInCents)}</span>
          </div>

          {!!discountDisplay && (
            <div className={styles.row}>
              <span>Discount</span>
              <span className={styles.price}>{discountDisplay}</span>
            </div>
          )}

          {!!serviceChargeDisplay && (
            <div className={styles.row}>
              <span>{serviceChargeName}</span>
              <span className={styles.price}>{serviceChargeDisplay}</span>
            </div>
          )}

          {salesTax > 0 && (
            <div className={styles.row}>
              <span>Sales Tax</span>
              <span className={styles.price}>{centsToDollars(salesTax)}</span>
            </div>
          )}

          {nonExemptableTaxLineItems}

          {tip > 0 && (
            <div className={styles.row}>
              <span>Gratuity</span>
              <span className={styles.price}>{centsToDollars(tip)}</span>
            </div>
          )}

          <div className={styles.row}>
            <span className={styles.totalRow}>Total</span>
            <span className={cn(styles.totalRow, styles.price)}>{centsToDollars(grandTotal)}</span>
          </div>
        </div>
      )}

      <div className={styles.payment}>

        {hasCardReceipt() && (
          <div>
            <div className={styles.paymentHeader}>payment info</div>
            <div className={styles.cardDataContainer}>
              {formattedCardData().map((group, idx) => {
                return (
                  <div className={styles.halfBlock} key={idx}>
                    {group.map((line, i) => <div key={i} className={styles.receiptLineItem}>{line}</div>)}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <h3 className={styles.paymentHeader}>Payment</h3>
        {isRichCheckoutOrder() && <div className={styles.paymentType}>Paid via the mobile app</div>}
        {!isRichCheckoutOrder() && <div className={styles.paymentType}><span>{order.paymentDisplay}</span><span>{centsToDollars(grandTotal)}</span></div>}
      </div>
    </div>
  )
}

ReceiptDetails.defaultProps = {
  menuName: '',
  order: {},
}

ReceiptDetails.propTypes = {
  menuName: PropTypes.string,
  order: PropTypes.object,
}

export default ReceiptDetails
